// =============================================================================
// SIREPPAR - Financiamiento (landing con selector de partido + cards de submódulos)
// =============================================================================

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { partidoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface SubmoduloCard {
  key: string;
  titulo: string;
  descripcion: string;
  ruta: (partidoId: string) => string;
  disponible: boolean;
  soloInterno?: boolean;
}

const SUBMODULOS: SubmoduloCard[] = [
  {
    key: 'cuenta-bancaria',
    titulo: 'Cuenta Bancaria',
    descripcion:
      'Declaración y actualización de la cuenta única partidaria (Art. 40 Ley XII-24).',
    ruta: (partidoId) => `/financiamiento/cuenta-bancaria/${partidoId}`,
    disponible: true,
  },
  {
    key: 'rendicion-anual',
    titulo: 'Rendición Anual',
    descripcion:
      'Estados contables anuales del partido (Art. 53 Ley XII-23).',
    ruta: (partidoId) => `/financiamiento/rendicion-anual/${partidoId}`,
    disponible: true,
    soloInterno: true,
  },
  {
    key: 'rendicion-campana',
    titulo: 'Rendición de Campaña',
    descripcion:
      'Ingresos, egresos y aportes de cada proceso electoral (Art. 53 inc. c, Ley XII-23).',
    ruta: (partidoId) => `/financiamiento/rendicion-campana/${partidoId}`,
    disponible: true,
    soloInterno: true,
  },
];

export default function FinanciamientoPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const esInterno = !user?.partidoId;

  const [selectedPartidoId, setSelectedPartidoId] = useState('');

  const partidoId = user?.partidoId || selectedPartidoId || undefined;

  // Solo cargamos la lista de partidos si el usuario es interno
  const { data: partidos = [] } = useQuery({
    queryKey: ['partidos-select'],
    queryFn: async () => {
      const response = await partidoService.list({ pageSize: 200 });
      return response.data.data?.items ?? response.data.data ?? [];
    },
    enabled: esInterno,
  });

  // Partido seleccionado (para mostrar su nombre en pantalla)
  const { data: partido } = useQuery({
    queryKey: ['partido', partidoId],
    queryFn: async () => {
      if (!partidoId) return null;
      const response = await partidoService.getById(partidoId);
      return response.data.data;
    },
    enabled: !!partidoId,
  });

  const handleCardClick = (submodulo: SubmoduloCard) => {
    if (!submodulo.disponible || !partidoId) return;
    if (submodulo.soloInterno && !esInterno) return;
    navigate(submodulo.ruta(partidoId));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Financiamiento</h1>
        <p className="mt-1 text-sm text-gray-500">
          Gestión económica y patrimonial del partido (Leyes XII-23 y XII-24).
        </p>
      </div>

      {/* Selector de partido — solo usuarios internos */}
      {esInterno && (
        <div className="bg-white rounded-lg shadow p-6 max-w-xl">
          <label htmlFor="partido-select" className="block text-sm font-medium text-gray-700 mb-2">
            Partido
          </label>
          <select
            id="partido-select"
            value={selectedPartidoId}
            onChange={(e) => setSelectedPartidoId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">— Seleccionar partido —</option>
            {(partidos as any[]).map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.denominacion} ({p.sigla})
              </option>
            ))}
          </select>
          {!selectedPartidoId && (
            <p className="mt-2 text-xs text-gray-500">
              Elegí un partido para ver sus submódulos de financiamiento.
            </p>
          )}
        </div>
      )}

      {/* Partido activo */}
      {partidoId && partido && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900">
          <span className="font-medium">Gestionando:</span>{' '}
          {(partido as any).denominacion} ({(partido as any).sigla})
        </div>
      )}

      {/* Cards de submódulos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SUBMODULOS.map((sub) => {
          const bloqueadoPorRol = !!sub.soloInterno && !esInterno;
          const clickable = sub.disponible && !!partidoId && !bloqueadoPorRol;

          return (
            <button
              key={sub.key}
              type="button"
              onClick={() => handleCardClick(sub)}
              disabled={!clickable}
              className={`text-left bg-white rounded-lg shadow p-5 transition-all border-2 ${
                clickable
                  ? 'border-transparent hover:border-blue-500 hover:shadow-md cursor-pointer'
                  : 'border-transparent opacity-60 cursor-not-allowed'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{sub.titulo}</h3>
                {!sub.disponible && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                    Próximamente
                  </span>
                )}
                {sub.disponible && !bloqueadoPorRol && !partidoId && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                    Elegí un partido
                  </span>
                )}
                {bloqueadoPorRol && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                    No disponible
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">{sub.descripcion}</p>
              {bloqueadoPorRol && (
                <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Aún no es fecha para realizar la rendición.
                </div>
              )}
              {clickable && (
                <div className="mt-4 text-sm font-medium text-blue-600">
                  Abrir →
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
